/**
 * Redux action creators for audio playback and playlist management.
 *
 * @module redux/audio/action
 * @author Snowfly
 * @version 1.0.0
 */
import {
    SET_LIST,
    SET_LIST_TYPE,
    PLAY_LIST,
    STOP_LIST,
    PLAY_AUDIO, STOP_AUDIO,
} from './audioTypes';

/**
 * Action creator function that sets the contents of a playlist.
 *
 * @function setPlaylistContents
 * @param {Object} payload - The data object containing the playlist information to be set.
 * @returns {Object} The action object with a type of SET_LIST and the provided payload.
 */
export const setPlaylistContents = (payload) => ({
    type: SET_LIST,
    payload,
});

/**
 * Function to create an action object for setting the playlist contents type in a Redux store.
 *
 * @function
 * @param {Object} payload - The data payload containing the playlist contents type information.
 * @returns {Object} The action object with a type property and the provided payload.
 */
export const setPlaylistContentsType = (payload) => ({
    type: SET_LIST_TYPE,
    payload,
});

/**
 * Action creator function for playing a playlist.
 *
 * This function generates an action object used to trigger the play of a specific playlist.
 * The generated action object includes the type indicating the action and the payload
 * containing additional information (e.g., playlist data or identifier).
 *
 * @function
 * @param {Object} payload - The data or identifier needed to play the specified playlist.
 * @returns {Object} An action object with a `type` property set to `PLAY_LIST` and
 * a `payload` property containing the passed data.
 */
export const playPlaylist = (payload) => ({
    type: PLAY_LIST,
    payload,
});

/**
 * Action creator function for stopping a list.
 *
 * This function generates an action object with a type of `STOP_LIST`.
 * It is typically dispatched to indicate that a list should be stopped
 * or its operation is halted.
 *
 * @returns {Object} An action object containing the type `STOP_LIST`.
 */
export const setListStopped = () => ({
    type: STOP_LIST,
});

/**
 * Sets the audio playing state in the application.
 *
 * @function
 * @name setAudioPlaying
 * @param {Object} payload - The data containing information about the audio playing state.
 * @returns {Object} An action object with a type of `PLAY_AUDIO` and the provided payload.
 */
export const setAudioPlaying = (payload) => ({
    type: PLAY_AUDIO,
    payload,
});

/**
 * Action creator function to signal that audio playback should be stopped.
 *
 * @function
 * @name setAudioStopped
 * @param {*} payload - The data to be dispatched along with the action, typically representing any necessary state or configurations related to stopping the audio.
 * @returns {Object} A Redux action object containing the `type` property set to `STOP_AUDIO` and the provided `payload`.
 */
export const setAudioStopped = (payload) => ({
    type: STOP_AUDIO,
    payload,
});

/**
 * Sets the playlist data and dispatches an action to update the playlist contents.
 *
 * @function setPlaylist
 * @param {Array|Object} playlist - The playlist data to be set, typically an array or object representing songs or media items.
 * @returns {Function} A function that performs the asynchronous dispatch action to update the application's state.
 */
export const setPlaylist = (playlist) => async (dispatch) => {
    dispatch(setPlaylistContents(playlist));
};

/**
 * Asynchronous action creator that dispatches an action to play a playlist.
 *
 * @function playAudioList
 * @async
 * @param {Object} payload - The data representing the playlist to be played.
 * @param {Function} dispatch - Redux dispatch function to dispatch actions to the store.
 *
 * @description
 * This function is used within a Redux setup to handle the playing of a playlist.
 * It takes a payload object containing playlist information and dispatches the
 * `playPlaylist` action with that payload.
 */
export const playAudioList = (payload) => async (dispatch) => {
    dispatch(playPlaylist(payload));
};

/**
 * A function to asynchronously handle audio playback functionality.
 * It dispatches an action to set the currently playing audio index.
 *
 * @function
 * @param {number} index - The index of the audio to be played.
 * @returns {Function} - A Redux thunk function that dispatches an action to manage the state.
 */
export const audioPlay = (index) => async (dispatch) => {
    dispatch(setAudioPlaying(index));
};

/**
 * Asynchronous action creator that stops audio playback for a given index.
 * This function dispatches an action to update the application state
 * to indicate that the audio at the specified index has been stopped.
 *
 * @param {number} index - The index of the audio to be stopped.
 * @returns {Function} A thunk function that accepts `dispatch` as an argument
 * and performs the stop audio operation.
 */
export const audioStop = (index) => async (dispatch) => {
    dispatch(setAudioStopped(index));
};

/**
 * Asynchronous action creator function that sets the playlist type in the application's state.
 *
 * This function takes a payload representing the desired playlist type and dispatches an action
 * to update the playlist contents type in the state. It utilizes Redux's dispatch function
 * to trigger the appropriate update in the state based on the provided payload.
 *
 * @function
 * @param {any} payload - The value representing the type of playlist being set.
 * @returns {Function} A thunk function (for asynchronous dispatch workflow) that dispatches
 * the action to update the playlist contents type.
 */
export const setPlaylistType = (payload) => async (dispatch) => {
    dispatch(setPlaylistContentsType(payload));
};

/**
 * Asynchronous action creator that stops the audio list playback.
 *
 * This function dispatches an action to update the application state,
 * indicating that the audio list playback has been stopped.
 *
 * The function is intended to be used as a Redux Thunk, allowing it
 * to perform asynchronous operations before dispatching actions
 * to the Redux store.
 *
 * @function audioListStop
 * @returns {Function} A Redux Thunk function that performs the dispatch operation.
 */
export const audioListStop = () => async (dispatch) => {
    dispatch(setListStopped());
};